/// <reference path="../../types/web/third_party/atmosphere.d.ts" />

export namespace BSMsg {
	const BSMSG_URL = "/a/bsmsg/";

	/**
	 * Floor for Atmosphere's own reconnect delay. Its default is 0, which makes
	 * a failing connection retry as fast as the network answers -- measured at
	 * roughly one request every 220ms, i.e. RTT, not a backoff.
	 */
	const RECONNECT_INTERVAL_MS = 1_000;

	/** First delay before re-subscribing after Atmosphere gives up, and the ceiling. */
	const RESUBSCRIBE_BASE_MS = 1_000;
	const RESUBSCRIBE_MAX_MS = 30_000;

	/**
	 * How long a connection must hold before the backoff is considered cleared.
	 * Comfortably longer than an accept-then-fail cycle and far shorter than any
	 * real session, so a flapping server still escalates to the ceiling.
	 */
	const CONNECTION_STABLE_MS = 10_000;

	/**
	 * Cap on messages held while the socket is down. An outage is exactly when
	 * this queue grows and nothing drains it, so it needs a bound; 100 is far
	 * above any burst a session produces between two connects.
	 */
	const MAX_QUEUED_MESSAGES = 100;

	/**
	 * Name of both the Web Lock that picks the one tab holding the socket and the
	 * BroadcastChannel the other tabs reach it through. Both are scoped to the
	 * origin, as the connection and its session cookie are.
	 */
	const RELAY_NAME = "bsmsg";

	export interface Message {
		channels: string[];
		data: string;
		permissions?: string[];
		organization?: string;
		crossOrganization?: boolean;
		uuid?: string;
		timestamp?: number;
	}

	export interface ListenData {
		channels: string[];
		onMessage: (msg: Message) => void;
		//no longer supported
		//crossOrganization?: boolean;
	}

	export interface ListenerCloser {
		close: () => void;
	}

	interface DupCheckData {
		uuid: string;
		time: number
	}

	interface ChannelUpdates {
		refreshChannels?: string[];
	}

	type SendData = Message | ChannelUpdates;

	function isChannelUpdate(payload: SendData): payload is ChannelUpdates {
		return (payload as ChannelUpdates).refreshChannels !== undefined;
	}

	function sameChannels(a: Set<string>, b: Set<string>): boolean {
		return a.size === b.size && [...a].every(c => b.has(c));
	}

	/**
	 * What the tabs of one origin say to each other. Only the leader posts
	 * {@code inbound} and {@code connected}, and only from what its own socket
	 * hands it; followers post {@code outbound} and never repost anything they
	 * receive. No frame is relayed twice, so no two tabs can echo each other.
	 *
	 * An outbound payload travels as the JSON its socket frame would carry, not
	 * as an object: postMessage structured-clones, which throws on a function or
	 * a DOM node that JSON.stringify would silently drop, so a send would
	 * otherwise succeed or throw depending on which tab happened to lead.
	 */
	type RelayFrame =
		| { kind: 'inbound', body: string }
		| { kind: 'outbound', payload: string }
		| { kind: 'connected' };

	/**
	 * Where a tab stands in its origin's relay. {@code pending} lasts from asking
	 * for the lock until the answer, which always arrives asynchronously.
	 */
	type RelayRole = 'idle' | 'pending' | 'follower' | 'leader';

	/**
	 * Without either API every tab falls back to a socket of its own: correct,
	 * just one connection per tab instead of per browser.
	 */
	function relaySupported(): boolean {
		return typeof BroadcastChannel === 'function'
			&& typeof navigator !== 'undefined'
			&& navigator.locks !== undefined
			&& typeof navigator.locks.request === 'function';
	}

	export class BSMsg {
		private request = new atmosphere.AtmosphereRequest!();
		private channels = new Set<string>();

		private isSubscribed = false;
		private socket: Atmosphere.Request | undefined;
		private listeners: ListenData[] = [];
		private isConnected = false;
		private preConnectMessages: SendData[] = [];
		declare private timeout: number;
		private resubscribeAttempts = 0;
		private resubscribeTimer = 0;
		private stableTimer = 0;
		private isReleasing = false;

		private role: RelayRole = 'idle';
		private relayChannel: BroadcastChannel | undefined;
		/** Withdraws this tab from the queue for the lock. */
		private relayWait: AbortController | undefined;
		/** Hands the lock back; set only while this tab leads. */
		private endLeadership: (() => void) | undefined;
		/**
		 * Bumped on every leave, so a lock answer that arrives for an earlier join
		 * is handed straight back instead of acting on state it no longer owns.
		 */
		private relayGeneration = 0;
		private watchingLifecycle = false;
		/** Set once the lock API has failed here; this tab then keeps a socket of its own. */
		private relayUnavailable = false;
		/**
		 * Sends another tab relayed while this one was still waiting to learn its
		 * role. See {@link onRelayFrame}.
		 */
		private heldForLeader: SendData[] = [];

		constructor() {
			this.request.contentType = "application/json";
			this.request.transport = 'websocket';
			// Atmosphere's own shared mode stays off. In 4.0.1 it relays between tabs
			// through localStorage under a once-a-second cookie heartbeat;
			// background-tab timer throttling stales that cookie, several tabs take
			// over the connection at once, and a tab that takes over keeps its child
			// storage listener and re-signals every relayed message. Two such tabs
			// echo each other without end, and a frozen tab queues the StorageEvents
			// until it runs out of memory. One socket per browser comes from the
			// relay instead -- see joinRelay.
			this.request.shared = false;
			// Atmosphere's reconnect defaults are a hot loop: reconnectInterval 0
			// and reconnectOnServerError true, so an unhealthy server is retried at
			// RTT speed indefinitely.
			//
			// reconnectInterval is the floor for every retry Atmosphere drives
			// itself, and is what bounds that loop. reconnectOnServerError is read
			// only on the XHR transports, so with the websocket pinned above it does
			// not act on the first failure: a 5xx closes the socket, Atmosphere
			// retries once and then downgrades to the streaming fallback, and only
			// there does the flag turn the next status into onError. It still earns
			// its place -- it is what stops the fallback transport re-entering the
			// same spin -- but recovery reaches scheduleResubscribe a transport and
			// a couple of seconds later than the flag alone would suggest.
			this.request.reconnectInterval = RECONNECT_INTERVAL_MS;
			this.request.reconnectOnServerError = false;
			this.request.onMessage = (response: Atmosphere.Response) => {
				const body = response.responseBody!;
				// Relay before dispatching, so a listener here that throws cannot
				// starve every other tab.
				if (this.role === 'leader') {
					this.postRelay({ kind: 'inbound', body });
				}
				this.dispatch(body);
			};
			this.request.onOpen = (response?: Atmosphere.Response) => {
				this.handleConnected();
			}
			// Atmosphere fires onOpen only for the first connection; every
			// subsequent reconnect fires onReopen. Without this the client stays
			// permanently marked disconnected after a single drop, queues every
			// send forever, and never re-registers its channels.
			this.request.onReopen = (request?: Atmosphere.Request, response?: Atmosphere.Response) => {
				this.handleConnected();
			}
			this.request.onClose = (response?: Atmosphere.Response) => {
				this.isConnected = false;
				// The connection did not last, so it must not clear the backoff.
				this.clearStableTimer();
			}
			this.request.onClientTimeout = (request?: Atmosphere.Request) => {
				this.isConnected = false;
				this.clearStableTimer();
			}
			// Atmosphere has given up for good by the time this fires: it has
			// exhausted its own retries on the websocket, downgraded to the fallback
			// transport and exhausted that too. No further onReopen will arrive, so
			// the subscription has to be released and a new one scheduled.
			this.request.onError = (response?: Atmosphere.Response) => {
				this.abandonSubscription();
				this.scheduleResubscribe();
			}
			// Unreachable in atmosphere.js 4.0.1 -- nothing ever assigns the
			// 'fail-to-reconnect' state that would dispatch it -- but it is part of
			// the documented API, so treat it the same way should a later release
			// start using it.
			this.request.onFailureToReconnect = (request?: Atmosphere.Request, response?: Atmosphere.Response) => {
				this.abandonSubscription();
				this.scheduleResubscribe();
			}
		}

		/**
		 * Drops the subscription so a later send, listen or registration refresh
		 * re-subscribes from scratch.
		 *
		 * Releasing it through Atmosphere is the load-bearing part.
		 * {@code atmosphere.subscribe} appends to a module-global request list that
		 * only {@code unsubscribe}/{@code unsubscribeUrl} ever prune, and its
		 * internal {@code _onError} leaves the dead request in place with its
		 * response, callbacks and closures still reachable. Subscribing again
		 * without releasing therefore ADDS a client rather than replacing one, so a
		 * server that keeps failing multiplies both the retained state in the tab
		 * and the request rate at the server.
		 */
		private abandonSubscription() {
			this.isConnected = false;
			this.isSubscribed = false;
			this.clearStableTimer();
			this.releaseAtmosphereRequest();
		}

		/**
		 * Closes whatever Atmosphere still holds for this url and removes it from
		 * that global list. Guarded because the callbacks fired during teardown run
		 * synchronously and must not re-enter this path.
		 */
		private releaseAtmosphereRequest() {
			if (this.isReleasing) {
				return;
			}
			this.isReleasing = true;
			try {
				// unsubscribeUrl closes the request AND splices it out; socket.close()
				// alone would stop the retries but leave the entry retained.
				// Guarded rather than called outright: atmosphere.js is loaded from a
				// CDN at runtime, so the build actually served is not pinned here.
				if (typeof atmosphere.unsubscribeUrl === 'function') {
					atmosphere.unsubscribeUrl(BSMSG_URL);
				} else if (this.socket && this.socket.close) {
					this.socket.close();
				}
			} catch {
				// Tearing down a connection that is already gone is not a failure, and
				// throwing here would strand isSubscribed and block every later retry.
			} finally {
				this.socket = undefined;
				this.isReleasing = false;
			}
		}

		/**
		 * Re-subscribes after a backoff instead of on the next send.
		 *
		 * Without this the retry cadence is set by whatever the page happens to do:
		 * the 4-minute registration refresh, or any user action, each re-opening
		 * immediately against a server that just refused. The delay doubles per
		 * consecutive failure to {@link RESUBSCRIBE_MAX_MS}. It is cleared only once
		 * a connection has HELD for {@link CONNECTION_STABLE_MS}, not on open --
		 * see {@link armStableTimer} for why opening alone proves nothing.
		 */
		private scheduleResubscribe() {
			if (this.resubscribeTimer) {
				return;
			}
			const backoff = Math.min(RESUBSCRIBE_BASE_MS * 2 ** this.resubscribeAttempts, RESUBSCRIBE_MAX_MS);
			this.resubscribeAttempts++;
			// Spread the retry over [backoff/2, backoff]. A server-side outage fails
			// every tab in the fleet at once, so an unjittered backoff would have
			// them all return in lockstep and meet the recovering pod as one spike.
			// Half the window is enough spread for that without ever retrying
			// sooner than half the delay the escalation just earned.
			const delay = Math.round(backoff * (0.5 + Math.random() * 0.5));
			this.resubscribeTimer = setTimeout(() => {
				this.resubscribeTimer = 0;
				// Nothing to reconnect for if every listener has gone and no send is
				// waiting; the next listen() or send() will open one on demand. A
				// leader reconnects regardless: the other tabs depend on its socket.
				if (this.role === 'leader' || this.listeners.length > 0 || this.preConnectMessages.length > 0) {
					this.subscribeNow();
				}
			}, delay);
		}

		/**
		 * Marks the socket live, registers this client's channels, and drains
		 * anything queued while it was down.
		 *
		 * The registration is re-sent on every connect, not just on reconnects: a
		 * reopen or a fresh subscribe is a new server-side connection that does not
		 * inherit the previous one's channels, and re-sending it on the very first
		 * open costs one frame. That uniformity is what lets the same path recover
		 * a subscription abandoned by {@link abandonSubscription}.
		 */
		private handleConnected() {
			this.isConnected = true;
			// Opening is not evidence the server is healthy: Atmosphere fires onOpen
			// as soon as the transport handshake completes, so a server that accepts
			// and then fails would reset the backoff on every cycle and pin it at the
			// base delay forever. Only a connection that stays up long enough to be
			// useful clears it.
			this.armStableTimer();
			const queued = this.preConnectMessages;
			this.preConnectMessages = [];
			try {
				if (this.channels.size > 0) {
					// Register before draining so queued sends land on a subscribed connection.
					this.pushChannels();
				}
				for (let i = 0; i < queued.length; i++) {
					try {
						this.socket!.push!(JSON.stringify(queued[i]));
					} catch (e) {
						// The socket died mid-drain. Keep everything not yet transmitted:
						// dropping it loses messages, and re-queueing the whole batch would
						// re-send the ones that already went out.
						this.preConnectMessages = this.capQueue(queued.slice(i).concat(this.preConnectMessages));
						throw e;
					}
				}
			} catch {
				// Recover through the same backoff every other failure uses. Marking
				// disconnected is not enough on its own: isSubscribed would stay true,
				// openIfNotSubscribed would keep returning early, and the client would
				// sit permanently silent -- a worse outcome than the storm this whole
				// change exists to stop.
				this.abandonSubscription();
				this.scheduleResubscribe();
			}
			// The other tabs' registrations died with the previous connection too,
			// and only they know their channels.
			if (this.isConnected && this.role === 'leader') {
				this.postRelay({ kind: 'connected' });
			}
		}

		/**
		 * Clears the backoff once a connection has held for {@link CONNECTION_STABLE_MS},
		 * which is the closest available proxy for "this connection was useful".
		 */
		private armStableTimer() {
			this.clearStableTimer();
			this.stableTimer = setTimeout(() => {
				this.stableTimer = 0;
				this.resubscribeAttempts = 0;
			}, CONNECTION_STABLE_MS);
		}

		private clearStableTimer() {
			if (this.stableTimer) {
				clearTimeout(this.stableTimer);
				this.stableTimer = 0;
			}
		}

		public send(message: Message) {
			this.sendData(message);
		}

		/**
		 * Pushes any outbound frame, or queues it if the socket is down.
		 *
		 * Takes {@link SendData} rather than {@link Message} because
		 * {@link pushChannels} routes channel registrations through here too -- the
		 * queueing rule below differs between the two.
		 */
		private sendData(payload: SendData) {
			this.openIfNotSubscribed();
			if (this.role === 'follower') {
				// The leader pushes, queues or drops it by the same rules as its own.
				this.postRelay({ kind: 'outbound', payload: JSON.stringify(payload) });
			} else if (this.isConnected) {
				this.socket!.push!(JSON.stringify(payload));
			} else if (!isChannelUpdate(payload)) {
				// Registrations are deliberately not queued. handleConnected sends a
				// fresh, current one on every connect, so queued snapshots would only
				// replay stale channel sets behind it.
				this.preConnectMessages.push(payload);
				this.preConnectMessages = this.capQueue(this.preConnectMessages);
			}
		}

		/**
		 * Bounds the pre-connect queue, dropping the oldest first.
		 *
		 * Unbounded, this grows for as long as an outage lasts and is retained for
		 * the life of the tab. Oldest-first is the right end to drop: these are
		 * status updates whose newest entry is the one still worth delivering.
		 */
		private capQueue(queue: SendData[]): SendData[] {
			return queue.length > MAX_QUEUED_MESSAGES ? queue.slice(queue.length - MAX_QUEUED_MESSAGES) : queue;
		}

		/**
		 * Does nothing; kept only because the client is exposed on globalThis.
		 *
		 * It used to hand the message to Atmosphere's pushLocal, which reaches other
		 * tabs only in shared mode -- and even then nothing here registered the
		 * onLocalMessage callback that would have delivered it to a listener. With
		 * shared mode off, pushLocal throws.
		 *
		 * @deprecated no tab ever received these messages; use {@link send}.
		 */
		public sendLocal(sendData: Message) {
		}

		public listen(listenData: ListenData): ListenerCloser {
			this.openIfNotSubscribed();
			this.addChannels(listenData.channels);
			this.listeners.push(listenData);
			return { close: () => this.removeListenData(listenData) };
		}

		public closeListener(listenerCloser: ListenerCloser) {
			listenerCloser.close();
		}

		private removeListenData(listenData: ListenData) {
			const index = this.listeners.indexOf(listenData);
			if (index > -1) {
				this.listeners.splice(index, 1);
				if (this.listeners.length === 0) {
					this.closeIfSubscribed();
				} else {
					const newChannels = new Set<string>();
					this.listeners.forEach(l => l.channels.forEach(c => newChannels.add(c)));
					// Compare contents, not just size. Removing a listener can only
					// shrink the union, so today a size difference is equivalent -- but
					// that holds only because nothing else ever deletes from
					// this.channels, which nothing enforces.
					if (!sameChannels(newChannels, this.channels)) {
						this.channels = newChannels;
						// Tell the server too, otherwise it keeps pushing messages
						// for channels nothing is listening to any more.
						this.pushChannels();
					}
				}
			}
		}

		private pushChannels() {
			if (this.timeout) {
				clearTimeout(this.timeout);
			}
			// 4 minutes
			this.timeout = setTimeout(() => this.pushChannels(), 240_000);
			this.sendData({ refreshChannels: [...this.channels] });
		}

		private addChannels(channels: string[]) {
			channels.forEach(c => this.channels.add(c));
			this.pushChannels();
		}

		/**
		 * Opens a subscription unless one is live or a backed-off retry is already
		 * pending. Deferring to that pending retry is what stops page activity from
		 * driving the reconnect rate: without the guard, every send during an
		 * outage re-opens immediately and the backoff never applies.
		 */
		private openIfNotSubscribed() {
			if (!this.relayUnavailable && relaySupported()) {
				// The lock decides whether this tab subscribes at all; a leader's
				// subscription is driven by lead() and its backoff from then on.
				this.joinRelay();
				return;
			}
			if (this.isSubscribed || this.resubscribeTimer) {
				return;
			}
			this.subscribeNow();
		}

		private subscribeNow() {
			this.request.url = BSMSG_URL;
			// Release before opening: Atmosphere never prunes its global request list
			// on error, so subscribing over a dead entry accumulates live retry loops.
			this.releaseAtmosphereRequest();
			this.socket = atmosphere.subscribe!(this.request);
			this.isSubscribed = true;
		}

		/**
		 * Joins this origin's relay: one tab -- the leader -- holds the socket, and
		 * every other tab reaches it over a BroadcastChannel.
		 *
		 * The leader is whoever holds the {@link RELAY_NAME} Web Lock. The browser
		 * grants it to one tab and passes it on only when that tab leaves, closes,
		 * navigates away or is discarded, so no timer decides leadership and
		 * background-tab throttling cannot produce two leaders. That race is what
		 * broke Atmosphere's shared mode.
		 *
		 * The server needs no help from here: it merges registrations per session
		 * and ages each channel out after five minutes, so a follower's 4-minute
		 * refresh passing through the leader keeps its channels alive, and a closed
		 * tab's lapse on their own.
		 */
		private joinRelay() {
			if (this.role !== 'idle') {
				return;
			}
			this.role = 'pending';
			const generation = this.relayGeneration;
			this.relayChannel = new BroadcastChannel(RELAY_NAME);
			this.relayChannel.onmessage = (event: MessageEvent) => this.onRelayFrame(event.data as RelayFrame);
			this.watchLifecycle();
			navigator.locks.request(RELAY_NAME, { ifAvailable: true }, lock => {
				if (generation !== this.relayGeneration) {
					// Left before the answer came; returning hands any lock straight back.
					return undefined;
				}
				if (lock) {
					return this.lead();
				}
				// Queue for the lock so this tab takes over if the leader goes. Done
				// before follow(), so nothing follow() relays can cost this tab its
				// place.
				const wait = new AbortController();
				this.relayWait = wait;
				navigator.locks.request(RELAY_NAME, { signal: wait.signal }, () =>
					generation === this.relayGeneration ? this.lead() : undefined
				).catch(() => this.relayFailed(generation));
				this.follow();
				return undefined;
			}).catch(() => this.relayFailed(generation));
		}

		/**
		 * Falls back to a socket of this tab's own when a lock request fails.
		 *
		 * The lock API can exist and still refuse -- in a sandboxed iframe without
		 * allow-same-origin, say, it rejects with SecurityError -- and a tab left
		 * waiting on a request that already failed would never subscribe at all.
		 * An AbortError from leaving the relay lands here too, but leaveRelay has
		 * bumped the generation by then, so it is ignored.
		 */
		private relayFailed(generation: number) {
			if (generation !== this.relayGeneration) {
				return;
			}
			this.relayUnavailable = true;
			this.leaveRelay();
			if (this.listeners.length > 0 || this.preConnectMessages.length > 0) {
				this.openIfNotSubscribed();
			}
		}

		/**
		 * Holds the lock until {@link leaveRelay}: the returned promise is what the
		 * lock waits on.
		 */
		private lead(): Promise<void> {
			this.role = 'leader';
			this.relayWait = undefined;
			// Queued here rather than sent: the socket below is not open yet.
			this.preConnectMessages = this.capQueue(this.preConnectMessages.concat(this.heldForLeader));
			this.heldForLeader = [];
			this.subscribeNow();
			return new Promise(resolve => {
				this.endLeadership = resolve;
			});
		}

		private follow() {
			this.role = 'follower';
			// Whoever does lead held its own copy of these, so they are not ours to send.
			this.heldForLeader = [];
			const queued = this.preConnectMessages;
			this.preConnectMessages = [];
			queued.forEach(payload => this.postRelay({ kind: 'outbound', payload: JSON.stringify(payload) }));
			if (this.channels.size > 0) {
				this.pushChannels();
			}
		}

		/**
		 * Gives up any part in the relay: the lock, a place in its queue, the
		 * socket if this tab led, and the channel. Listeners and channels are kept,
		 * so joining again picks up where this left off.
		 */
		private leaveRelay() {
			if (this.role === 'idle') {
				return;
			}
			this.relayGeneration++;
			if (this.relayWait) {
				this.relayWait.abort();
				this.relayWait = undefined;
			}
			if (this.role === 'leader') {
				if (this.resubscribeTimer) {
					clearTimeout(this.resubscribeTimer);
					this.resubscribeTimer = 0;
				}
				this.abandonSubscription();
			}
			if (this.endLeadership) {
				this.endLeadership();
				this.endLeadership = undefined;
			}
			if (this.relayChannel) {
				this.relayChannel.close();
				this.relayChannel = undefined;
			}
			this.heldForLeader = [];
			this.role = 'idle';
		}

		private onRelayFrame(frame: RelayFrame) {
			if (frame.kind === 'inbound') {
				if (this.role === 'follower') {
					this.dispatch(frame.body);
				}
			} else if (frame.kind === 'outbound') {
				const payload: SendData = JSON.parse(frame.payload);
				if (this.role === 'leader') {
					this.sendData(payload);
				} else if (this.role === 'pending' && !isChannelUpdate(payload)) {
					// The lock is granted at once but the grant reaches the page later, so
					// another tab can learn it follows -- and relay its sends -- while the
					// tab that will lead still reads as pending. Every pending tab holds a
					// copy; lead() sends it and follow() drops it, and only one tab leads.
					// Registrations are not held: every follower re-sends one on connect.
					this.heldForLeader = this.capQueue(this.heldForLeader.concat(payload));
				}
			} else if (frame.kind === 'connected') {
				if (this.role === 'follower' && this.channels.size > 0) {
					this.pushChannels();
				}
			}
		}

		private postRelay(frame: RelayFrame) {
			if (this.relayChannel) {
				this.relayChannel.postMessage(frame);
			}
		}

		/**
		 * A frozen tab leaves the relay and rejoins on resume.
		 *
		 * Frozen, a leader would hold the lock and the socket while running none of
		 * the code that serves them, and a follower would queue every frame it was
		 * sent until it thawed -- the same failure as the shared-mode echo, only
		 * slower. Leaving closes the channel, so nothing queues, and passes the
		 * lock to a tab that can still run.
		 */
		private watchLifecycle() {
			if (this.watchingLifecycle) {
				return;
			}
			this.watchingLifecycle = true;
			document.addEventListener('freeze', () => this.leaveRelay());
			document.addEventListener('resume', () => {
				if (this.listeners.length > 0 || this.preConnectMessages.length > 0) {
					this.joinRelay();
				}
			});
		}

		private dispatch(body: string) {
			const message: Message = JSON.parse(body);
			this.listeners.forEach(l => {
				if (l.channels.some(s => message.channels.includes(s))) {
					l.onMessage(message);
				}
			});
		}

		/**
		 * Tears everything down when the last listener goes.
		 *
		 * The condition covers a pending retry as well as a live subscription, and
		 * the two are mutually exclusive: {@link abandonSubscription} clears
		 * isSubscribed before {@link scheduleResubscribe} arms the timer. Testing
		 * isSubscribed alone would therefore skip this entire body in exactly the
		 * state that most needs it -- leaving the 4-minute refresh armed, which
		 * re-subscribes through openIfNotSubscribed a client the page deliberately
		 * closed, and leaving a queue whose non-emptiness makes the pending retry
		 * re-open it too. A follower holds neither, so its place in the relay
		 * counts as well.
		 */
		private closeIfSubscribed() {
			if (!this.isSubscribed && !this.resubscribeTimer && this.role === 'idle') {
				return;
			}
			// A leader leaving hands the lock, and with it the socket, to the next
			// tab in the queue.
			this.leaveRelay();
			if (this.timeout) {
				clearTimeout(this.timeout);
				this.timeout = 0;
			}
			if (this.resubscribeTimer) {
				clearTimeout(this.resubscribeTimer);
				this.resubscribeTimer = 0;
			}
			this.clearStableTimer();
			this.resubscribeAttempts = 0;
			this.releaseAtmosphereRequest();
			this.isSubscribed = false;
			this.isConnected = false;
			this.preConnectMessages = [];
		}
	}
}

var bsmsg = new BSMsg.BSMsg();

export { bsmsg };

(globalThis as any).BSMsg = BSMsg;
(globalThis as any).bsmsg = bsmsg;
